import React from 'react';
import { Flex, Typography, theme } from 'antd';

import { usePrerenderToken } from '../../../hooks/usePrerenderUser';
import { CfFlowResponse } from '../api/cloudflareFlowApi';
import { cfFailureCopyFor } from '../cfFailureCopy';
import { type CfNexusOpening, errorShownOf } from '../cfNexusOpening';
import { screenNexusPrompt } from '../nexus/screenNexusPrompt';
import { redactCfSecrets } from '../redactCfSecrets';
import CloudflareFailureCard from './CloudflareFailureCard';

const { Paragraph, Text } = Typography;

export type CloudflareErrorCardProps = {
  flow: CfFlowResponse;
  /**
   * The opening's `prefillText` is the filled-in first message. Nexus never
   * sends it; the customer reads it and can edit or delete it first.
   */
  onOpenNexus?: (opening: CfNexusOpening) => void;
};

/**
 * One card for every failure (PRE-3292 / F4).
 *
 * The variant comes from **`rollbackOutcome`, not `status`**: a clean rollback
 * and a failed one both leave the flow in `deploy_failed`, so branching on the
 * status would tell a customer whose account still holds our leftovers that
 * everything was tidied up.
 *
 * A `preflight_failed` flow reaches this card too, and it must never say
 * anything was undone — nothing had been changed yet. That is why the
 * "what we undid" line is driven by the rollback outcome rather than by the
 * presence of an error.
 *
 * The **leftovers list is driven by the leftovers**, not by the outcome. A
 * rollback that succeeded can still keep something on purpose: a DNS record we
 * created stays, because deleting it would stop the hostname resolving
 * (PRE-3285). Reading the outcome instead hid that record behind the line that
 * says nothing was half-changed — the one thing this card must never get wrong.
 *
 * What was undone or left behind sits between the message and the action,
 * because the failed-rollback action points at "the items above". The
 * put-back line is skipped when the copy says its message already states the
 * undo (`statesTheUndo`), so the card never says it twice.
 */
const CloudflareErrorCard = ({ flow, onOpenNexus }: CloudflareErrorCardProps) => {
  const { token } = theme.useToken();
  // The deployment puts this value in a Cloudflare Worker secret, so a leftover
  // the server builds can quote it back. Leftovers are drawn here and quoted
  // into the Nexus summary, which leaves for a LangGraph thread. No shape rule
  // can find 20 characters of base62, so the value the dashboard already holds
  // goes by exact match.
  const prerenderToken = usePrerenderToken();
  const redact = (value: string) => redactCfSecrets(value, [prerenderToken]);

  const { message, action, statesTheUndo } = cfFailureCopyFor(
    flow.errorDetail?.reason,
    flow.domain,
    flow.rollbackOutcome,
  );
  const leftovers = (flow.errorDetail?.leftovers ?? []).map(redact);

  return (
    <CloudflareFailureCard
      title="We couldn't complete your setup"
      askNexusLabel="Ask Nexus: Your AI Integration Assistant"
      guidesLabel="View step-by-step guides"
      onAskNexus={() =>
        onOpenNexus?.({
          fromStep: 'error',
          errorShown: errorShownOf(flow),
          prefillText: screenNexusPrompt({
            flow,
            screen: flow.screen,
            verificationRetry: 'none',
            knownSecrets: [prerenderToken],
          }),
        })
      }
    >
      <Paragraph style={{ marginBottom: 0 }}>{message}</Paragraph>

      {flow.rollbackOutcome === 'succeeded' && leftovers.length === 0 && !statesTheUndo && (
        <Paragraph style={{ marginBottom: 0 }}>
          We put your Cloudflare settings back the way they were, so nothing
          is left half-changed.
        </Paragraph>
      )}

      {leftovers.length > 0 && (
        <Flex vertical gap={token.marginXXS}>
          <Paragraph style={{ marginBottom: 0 }}>
            Some changes are still in your Cloudflare account, and you will
            need to remove them yourself:
          </Paragraph>
          <ul style={{ marginBottom: 0 }}>
            {leftovers.map((leftover) => (
              <li key={leftover}>
                <Text>{leftover}</Text>
              </li>
            ))}
          </ul>
        </Flex>
      )}

      <Paragraph style={{ marginBottom: 0 }}>{action}</Paragraph>
    </CloudflareFailureCard>
  );
};

export default CloudflareErrorCard;
